import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    BankcardTicketsFacade,
    BankcardTransaction,
    RelatedCardService,
    PaymentMethodsService,
    ApplicationFacade
} from '@ridango/components';
import {
    AddCardButton,
    LogService,
    Notification,
    NotificationService,
    PaymentMethod,
    PaymentPortalService,
    ShoppingCart
} from '@ridango/core';
import { takeUntil } from 'rxjs/operators';
import { BasePageComponent } from '../../../shared/components/base-page/base-page.component';
import { Subject } from 'rxjs';
import { HelperService } from '../../../shared/services/helper.service';
import Env from '@ridango/core/env';
import { CartPaymentMethod, CartPaymentMethodsDetails } from '@ridango/components/lib/purchase/purchase.model';
import { EcommFormService } from '@ridango/core/lib/services';
import { TransactionMessage } from '@ridango/core/lib/models/ecomm-form.interface';

@Component({
    selector: 'gl-bank-cards-add-edit',
    templateUrl: './bank-cards-add-edit.component.html',
    styleUrls: ['./bank-cards-add-edit.component.scss']
})
export class BankCardsAddEditComponent extends BasePageComponent implements OnInit, OnDestroy {
    @Output() addCardButton: EventEmitter<AddCardButton> = new EventEmitter<AddCardButton>();
    @ViewChild('iframe', { static: false }) iframe: ElementRef;
    private componentDestroyed$ = new Subject<void>();
    private transaction: BankcardTransaction;
    private isDarkMode = false;
    displayIframe = true;
    hostApp: string = this.env.CEMV_HOST_APP;
    tpsEndpoint: string;
    ticketsError$ = this.bankcardTicketsFacade.ticketsError$;
    ticketsLoading$ = this.bankcardTicketsFacade.ticketsLoading$;
    private triesLeft = 3;
    public paymentPortalProviderMethod: PaymentMethod;
    public posMethod: CartPaymentMethod;
    private shoppingCart: ShoppingCart;

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private helperService: HelperService,
        private bankcardTicketsFacade: BankcardTicketsFacade,
        private relatedCardService: RelatedCardService,
        private paymentPortalService: PaymentPortalService,
        private notifications: NotificationService,
        private router: Router,
        private ls: LogService,
        private translate: TranslateService,
        private paymentMethodsService: PaymentMethodsService,
        private ecommFormService: EcommFormService,
        private applicationFacade: ApplicationFacade
    ) {
        super(helperService);
        this.tpsEndpoint = this.env.TPS_BANKCARD_IFRAME_URL;
    }

    ngOnInit(): void {
        this.ecommFormService
            .watchThemeChanges(
                this.applicationFacade.highContrast$,
                this.componentDestroyed$,
                () => this.iframe,
                () => this.displayIframe
            )
            .subscribe((isDarkMode) => {
                this.isDarkMode = isDarkMode;
            });

        this.hostApp = this.env.CEMV_HOST_APP;
        this.translate?.onLangChange?.subscribe(({ lang }) => {
            if (this.transaction && this.transaction.lang !== lang) {
                this.newSearch();
            }
        });
        this.bankcardTicketsFacade.fetchTransaction(this.env.REGION_ID);
        this.bankcardTicketsFacade.transaction$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((transaction: BankcardTransaction) => {
                if (transaction?.hmac) {
                    if (this.iframe && this.iframe.nativeElement.src !== this.tpsEndpoint) {
                        this.iframe.nativeElement.src = this.tpsEndpoint;
                    }

                    this.transaction = transaction;
                    this.transaction.hostApp = this.hostApp;
                    this.transaction.useNewCardTokenForm = true;

                    this.transaction.lang = this.translate.currentLang;
                }
            });
        this.bankcardTicketsFacade.error$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                this.handleError(error);
            }
        });
        this.fetchMethods();
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
        this.notifications.deleteAll(this);
        this.bankcardTicketsFacade.cleanState();
    }

    sendTransactionInfoToTPS() {
        this.iframe.nativeElement.contentWindow.postMessage(this.getTransactionMessage(), this.tpsEndpoint);
    }

    private getTransactionMessage(): TransactionMessage {
        return this.ecommFormService.getTransactionMessage(this.transaction, this.isDarkMode, {
            useShortCardTokenForm: false,
            use3DSCardTokenForm: true,
            permissiveSubmitButton: false,
            placeholders: true
        });
    }

    handleError(e: any) {
        this.displayIframe = false;
        if (e.error.description) {
            this.notifications.add(new Notification(this.notifications.notificationTypes.ERROR, e.error.description));
        } else {
            if (!e.error.sources) {
                this.notifications.add(new Notification(this.notifications.notificationTypes.ERROR, e.error));
            }
        }
    }
    newSearch() {
        if (this.iframe) {
            this.iframe.nativeElement.src = '';
        }
        this.transaction = null;
        this.bankcardTicketsFacade.cleanState();
        this.notifications.deleteAll(this);
        this.triesLeft = 3;
        this.bankcardTicketsFacade.fetchTransaction(this.env.REGION_ID);
    }

    @HostListener('window:message', ['$event'])
    iframeMessage(event) {
        if (event.data && this.displayIframe) {
            if ('ready' in event.data && event.data.ready) {
                this.sendTransactionInfoToTPS();
            } else if (event.data.responseCodeName && event.data.responseCodeName === 'APPROVED') {
                this.notifications.deleteAll(this);
                this.onFoundedCardPan();
            }
        }
    }

    fetchMethods() {
        this.paymentMethodsService.fetchStarted();
        this.paymentMethodsService.fetchAllMethods().subscribe(
            (paymentMethods) => {
                this.paymentPortalProviderMethod = paymentMethods[0];
                this.paymentMethodsService.getPaymentMethods().subscribe(
                    (response: CartPaymentMethodsDetails) => {
                        this.posMethod = response.methods.find((m) => m.code === 'visa_mc');
                    },
                    (error) => {
                        this.handleError(error);
                    }
                );
            },
            (e) => {
                this.paymentMethodsService.fetchFailed(e);
            }
        );
    }

    addCard() {
        this.paymentPortalService
            .initiatePayment({
                shoppingCartId: this.shoppingCart?.shopping_cart_id,
                paymentId: this.paymentPortalProviderMethod.payment_id,
                bankCardPersonalize: true
            })
            .subscribe(
                () => {
                    console.log('initiatePayment success');
                },
                (error) => {
                    console.error('initiatePayment error', error);
                }
            );
    }

    onFoundedCardPan() {
        this.displayIframe = false;
        this.relatedCardService
            .createShoppingCartForPersonalizeBankCard(String(this.transaction.transactionId))
            .subscribe(
                (data) => {
                    this.shoppingCart = data;
                    this.addCardButton.emit({ amount: data?.total, disabled: false });
                },
                (error) => {
                    this.handleError(error);
                }
            );
    }

    back() {
        this.router.navigate([this.translate.currentLang, 'account', 'related-cards']);
    }
}
